import { useMemo, useState } from "react";
import { useTripStore } from "../store/tripStore";
import {
  collectUsedTimezoneIds,
  formatShortTimezoneLabel,
  formatTimezoneLabel,
} from "../utils/timezoneDisplay";
import { TimezoneCatalogModal } from "./TimezoneCatalogModal";
import { Button } from "./Button";

const CATALOG_OPTION = "__catalog__";

export function TripTimezoneSelect({
  value,
  date,
  onChange,
  disabled = false,
  sameAsZone,
  id,
  variant = "field",
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: {
  value: string;
  date: string;
  onChange: (timezoneId: string) => void;
  disabled?: boolean;
  sameAsZone?: string;
  id?: string;
  /**
   * `chip` is the compact inline form used in sentence-style rows: short
   * labels ("London · UTC+1") and the full catalogue as the last option
   * instead of a separate "+" button.
   */
  variant?: "field" | "chip";
  "aria-label"?: string;
  "data-testid"?: string;
}) {
  const trip = useTripStore((state) => state.trip);
  const schedule = useTripStore((state) => state.schedule);
  const setTripTimezones = useTripStore((state) => state.setTripTimezones);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const effectiveDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? date
    : (trip?.startDate ?? "2000-01-01");

  const compact = variant === "chip";
  const zones = useMemo(() => {
    if (!trip) return [value];
    return [
      ...new Set([
        trip.defaultTimezone,
        ...trip.timezoneIds,
        ...collectUsedTimezoneIds(trip.defaultTimezone, schedule),
        value,
      ]),
    ];
  }, [schedule, trip, value]);

  const selectFromCatalog = (timezoneId: string) => {
    if (trip && timezoneId !== trip.defaultTimezone) {
      setTripTimezones(trip.defaultTimezone, [...trip.timezoneIds, timezoneId]);
    }
    onChange(timezoneId);
    setCatalogOpen(false);
  };

  return (
    <>
      <div className={compact ? "inline-flex min-w-0" : "flex min-w-0 gap-2"}>
        <select
          id={id}
          aria-label={ariaLabel}
          value={value}
          onChange={(event) => {
            if (event.target.value === CATALOG_OPTION) {
              setCatalogOpen(true);
              return;
            }
            onChange(event.target.value);
          }}
          disabled={disabled}
          data-testid={dataTestId}
          className={
            compact
              ? "h-8 min-w-0 cursor-pointer rounded-md border-0 bg-subtle pl-2.5 pr-7 text-sm font-semibold text-primary outline-none transition-colors hover:bg-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info disabled:cursor-default"
              : "select-base min-w-0 flex-1 pr-8"
          }
        >
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {compact
                ? formatShortTimezoneLabel(zone, effectiveDate)
                : sameAsZone === zone
                  ? `Same as start — ${formatTimezoneLabel(zone, effectiveDate)}`
                  : formatTimezoneLabel(zone, effectiveDate)}
            </option>
          ))}
          {compact && !disabled && (
            <option value={CATALOG_OPTION}>Other time zone…</option>
          )}
        </select>
        {!disabled && !compact && (
          <Button
            variant="secondary"
            size="lg"
            icon="add"
            onClick={() => setCatalogOpen(true)}
            className="shrink-0 rounded-lg text-secondary hover:border-sky-300 hover:bg-sky-50 hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            aria-label="Add timezone from full catalogue"
            title="Add timezone…"
          />
        )}
      </div>
      <TimezoneCatalogModal
        open={catalogOpen}
        date={effectiveDate}
        onClose={() => setCatalogOpen(false)}
        onSelect={selectFromCatalog}
      />
    </>
  );
}
